<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import moment from 'moment'
import { useProjectStore } from '@/stores/project'
import { useOrgStore } from '@/stores/org'
import { useBoundaryStore } from '@/stores/boundary'
import { useJoinLinksStore } from '@/stores/joinLinks'
import { Printer, X } from 'lucide-vue-next'

import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useQrCode } from '@/composables/useQrCode'
import { buildStaticMapUrl } from '@/utils/mapbox/staticMap'

const projectStore = useProjectStore()

const orgStore = useOrgStore()

const boundaryStore = useBoundaryStore()

const joinLinksStore = useJoinLinksStore()
const route = useRoute()

const projectId = computed(() => parseInt(route.params.project_id))
const orgId = computed(() => parseInt(route.params.org_id))
const project = computed(() => projectStore.current)
const org = computed(() => orgStore.getSelectedOrg || orgStore.current)
const orgName = computed(() => org.value?.organization_name || project.value?.organization_name || '')
const orgLogo = computed(() => org.value?.avatar_url || orgStore.current?.avatar_url || null)
const orgInitial = computed(() => (orgName.value.trim()[0] || '?').toUpperCase())

const link = computed(() =>
  joinLinksStore.getLinks(projectId.value).find((l) => l.id === Number(route.params.link_id)) || null
)

const isLoading = ref(true)
const boundary = ref(null)
const coverFailed = ref(false)
const orgLogoFailed = ref(false)
const { svg } = useQrCode(() => link.value?.url)

const coverImage = computed(() =>
  project.value?.cover_type === 'image' && project.value.cover_url && !coverFailed.value ? project.value.cover_url : null
)
const coverEmoji = computed(() => (!coverImage.value && project.value?.cover_type === 'emoji' ? project.value.emoji : null))
const coverMap = computed(() =>
  !coverImage.value && !coverEmoji.value && boundary.value ? buildStaticMapUrl(boundary.value, { width: 640, height: 320 }) : null
)

onMounted(async () => {
  document.documentElement.classList.add('join-print')
  try {
    if (orgId.value) orgStore.setSelectedOrg(orgId.value)
    await Promise.all([
      projectStore.fetchProject(projectId.value),
      orgStore.fetchOrg(orgId.value),
      orgStore.orgs?.length ? null : orgStore.fetchOrgs(),
      projectStore.fetchMembers(projectId.value),
      joinLinksStore.fetchLinks(projectId.value),
      boundaryStore.fetchBoundariesByIds([projectId.value]).then((features) => {
        boundary.value = features?.[0] || null
      }),
    ])
  } finally {
    isLoading.value = false
  }
})

const closeTab = () => window.close()
const printSheet = () => window.print()

onBeforeUnmount(() => {
  document.documentElement.classList.remove('join-print')
})
</script>

<template>
  <div class="min-h-screen bg-neutral-200 py-8 print:bg-white print:py-0">
    <div v-if="isLoading" class="flex h-64 items-center justify-center">
      <Spinner class="size-8" />
    </div>

    <div v-else-if="!link || !project" class="mx-auto max-w-md rounded-xl bg-white p-8 text-center text-neutral-700 shadow">
      This join link no longer exists.
    </div>

    <div
      v-else
      class="print-sheet mx-auto flex flex-col bg-white text-neutral-900 shadow-xl"
      style="width: 210mm; min-height: 297mm; padding: 16mm 18mm; box-sizing: border-box; print-color-adjust: exact; -webkit-print-color-adjust: exact"
      data-testid="join-print-sheet"
    >
      <header class="flex items-center gap-4">
        <img
          v-if="orgLogo && !orgLogoFailed"
          :src="orgLogo"
          alt=""
          class="shrink-0 rounded-xl bg-neutral-100 object-contain"
          style="width: 14mm; height: 14mm"
          data-testid="print-org-logo"
          @error="orgLogoFailed = true"
        />
        <div
          v-else
          class="flex shrink-0 items-center justify-center rounded-xl bg-neutral-900 text-xl font-bold text-white"
          style="width: 14mm; height: 14mm"
          data-testid="print-org-initial"
        >
          {{ orgInitial }}
        </div>
        <div class="min-w-0">
          <p class="text-xs font-semibold uppercase tracking-widest text-neutral-500">Organization</p>
          <p class="truncate text-lg font-semibold">{{ orgName }}</p>
        </div>
      </header>

      <div
        v-if="coverImage || coverEmoji || coverMap"
        class="mt-6 w-full overflow-hidden rounded-2xl bg-neutral-100"
        style="height: 62mm"
        data-testid="print-cover"
      >
        <img v-if="coverImage" :src="coverImage" alt="" class="size-full object-cover" @error="coverFailed = true" />
        <div v-else-if="coverEmoji" class="flex size-full items-center justify-center" style="font-size: 36mm; line-height: 1">
          {{ coverEmoji }}
        </div>
        <img v-else :src="coverMap" alt="" class="size-full object-cover" />
      </div>

      <div class="mt-6 flex items-start gap-4">
        <div
          v-if="project.emoji"
          class="flex shrink-0 items-center justify-center rounded-xl bg-neutral-100"
          style="width: 12mm; height: 12mm; font-size: 7mm; line-height: 1"
          data-testid="print-project-emoji"
        >
          {{ project.emoji }}
        </div>
        <div class="min-w-0">
          <h1 class="text-4xl font-bold leading-tight">{{ project.project_name }}</h1>
          <p v-if="project.project_desc" class="mt-2 line-clamp-4 text-base text-neutral-600">{{ project.project_desc }}</p>
        </div>
      </div>

      <div class="mt-8 flex flex-col items-center">
        <div class="[&>svg]:h-auto [&>svg]:w-full" style="width: 92mm" v-html="svg"></div>
        <p class="mt-5 text-2xl font-semibold">Scan to join</p>
        <p class="mt-1 text-center text-sm text-neutral-600">
          Point your phone camera at the code, or open the link below.
        </p>
        <p class="mt-3 break-all text-center font-mono text-sm">{{ link.url }}</p>
        <p v-if="link.expires_at" class="mt-3 text-sm text-neutral-600" data-testid="print-expiry">
          Valid until {{ moment(link.expires_at).format('D MMM YYYY, HH:mm') }}
        </p>
      </div>

    </div>

    <div class="fixed bottom-6 right-6 flex gap-2 print:hidden">
      <Button type="button" variant="secondary" @click="closeTab">
        <X class="mr-1.5 size-4" />
        Close
      </Button>
      <Button type="button" @click="printSheet">
        <Printer class="mr-1.5 size-4" />
        Print
      </Button>
    </div>
  </div>
</template>
